import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ModuleStateDB } from '../data/datatypes';
import { saveModuleState } from '../services/DBService/DbService';
import { StateProperty } from '@app/common';
import { RootState } from './store';

type ElementValue = string | number | boolean;

type SetElementValuePayload = {
    key: string;
    value: ElementValue;
};

export interface ModuleState {
    transientStateProperties: StateProperty[];
    currentExerciseUrl?: string;
    currentModuleState: ModuleStateDB;
}

const initialState: ModuleState = {
    transientStateProperties: [],
    currentModuleState: {
        _id: null,
        finished: {},
        modifiers: [],
        elementValues: {}
    }
};

const saveQueues = new Map<string, Promise<void>>();

const enqueueModuleStateSave = (moduleStateDB: ModuleStateDB): Promise<void> => {
    const moduleId = moduleStateDB._id || 'module_without_id';
    const previousSave = saveQueues.get(moduleId) || Promise.resolve();
    const currentSave = previousSave
        .catch(() => undefined)
        .then(() => saveModuleState(moduleStateDB));

    saveQueues.set(moduleId, currentSave);

    return currentSave.finally(() => {
        if (saveQueues.get(moduleId) === currentSave) {
            saveQueues.delete(moduleId);
        }
    });
};

export const setCurrentModuleState = createAsyncThunk<
    ModuleStateDB,
    ModuleStateDB,
    { state: RootState, rejectValue: Error }
>(
    'moduleState/setCurrentModuleState',
    async (moduleStateDB) => {
        await enqueueModuleStateSave(moduleStateDB);
        return moduleStateDB;
    }
);

export const setElementValue = createAsyncThunk<
    ModuleStateDB,
    SetElementValuePayload,
    { state: RootState, rejectValue: Error }
>(
    'moduleState/setElementValue',
    async (_payload, { getState }) => {
        const latestModuleState = getState().moduleState.currentModuleState;
        await enqueueModuleStateSave(latestModuleState);
        return latestModuleState;
    }
);

export const setModuleModifiers = createAsyncThunk<
    ModuleStateDB,
    string[],
    { state: RootState, rejectValue: Error }
>(
    'moduleState/setModuleModifiers',
    async (_modifiers, { getState }) => {
        const latestModuleState = getState().moduleState.currentModuleState;
        await enqueueModuleStateSave(latestModuleState);
        return latestModuleState;
    }
);

const moduleState = createSlice({
    name: 'moduleState',
    initialState,
    reducers: {
        setTransientStateProperties: (state, action: PayloadAction<StateProperty[]>) => {
            state.transientStateProperties = action.payload;  
        },
        setCurrentExerciseUrl: (state, action: PayloadAction<string | undefined>) => {
            state.currentExerciseUrl = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(setCurrentModuleState.pending, (state, action) => {
            state.currentModuleState = action.meta.arg;
        });
        builder.addCase(setElementValue.pending, (state, action) => {
            state.currentModuleState.elementValues[action.meta.arg.key] = action.meta.arg.value;
        });
        builder.addCase(setModuleModifiers.pending, (state, action) => {
            state.currentModuleState.modifiers = action.meta.arg;
        });
    }
});

export const { setTransientStateProperties, setCurrentExerciseUrl } = moduleState.actions;

export default moduleState;
